import type { galleryFr, socialFr } from './social.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for the social feed (#52) and the low-code template gallery (#54). Same keys as `social.fr.ts`. */
export const socialEn: DeepStrings<typeof socialFr> = {
  page: {
    title: 'Project activity',
    short: 'Activity',
    subtitle:
      'What happened in {{project}} and its jobs, workflows, experiments and endpoints. React, comment, follow.',
  },
  summary: {
    contributions: 'Contributions',
    activeDays: 'Active days',
    since: 'Since',
  },
  feed: {
    label: 'Activity feed',
    loadMore: 'Load more',
    platform: 'The platform',
    times_one: '×{{count}}',
    times_other: '×{{count}}',
    unavailable: 'The activity feed is not enabled on this installation.',
    empty: {
      title: 'No activity yet',
      description: 'Creating a job, running it or following a resource shows up here.',
    },
  },
  verbs: {
    projectcreation: 'created the project',
    projectupdate: 'updated the project',
    projectdeletion: 'deleted the project',
    jobcreation: 'created the job',
    jobupdate: 'updated the job',
    jobdeletion: 'deleted the job',
    jobrun: 'ran the job',
    workflowcreation: 'created the workflow',
    workflowupdate: 'updated the workflow',
    workflowdeletion: 'deleted the workflow',
    workflowrun: 'ran the workflow',
    experimentcreation: 'created the experiment',
    experimentupdate: 'updated the experiment',
    experimentdeletion: 'deleted the experiment',
    endpointcreation: 'created the endpoint',
    endpointupdate: 'updated the endpoint',
    endpointdeletion: 'deleted the endpoint',
    userfollow: 'follows',
    fallback: 'acted on {{type}}',
  },
  followVerbs: {
    follow: 'follows {{type}}',
    unfollow: 'no longer follows {{type}}',
  },
  types: {
    project: 'the project',
    job: 'the job',
    workflow: 'the workflow',
    experiment: 'the experiment',
    endpoint: 'the endpoint',
    catalog: 'the catalog',
    database: 'the database',
    table: 'the table',
    user: 'the user',
    resource: 'the resource',
  },
  typeNames: {
    project: 'Project',
    job: 'Job',
    workflow: 'Workflow',
    experiment: 'Experiment',
    endpoint: 'Endpoint',
    catalog: 'Catalog',
    database: 'Database',
    table: 'Table',
    user: 'User',
  },
  profile: {
    subtitle: 'Your feed: what you did and the activity of what you follow.',
    tabs: { feed: 'My feed', following: 'Following' },
    followingHelp: 'What you follow: its activity shows up in your feed.',
    columns: { type: 'Type', name: 'Resource' },
    unfollow: 'Unfollow',
    unfollowed: 'Unfollowed.',
    empty: {
      title: 'You follow nothing yet',
      description: 'The “Follow” button of a project, a job or a workflow adds it here.',
    },
  },
  reactions: {
    label: '{{reaction}}: {{count}}',
    names: {
      like: 'Like',
      celebrate: 'Celebrate',
      insightful: 'Insightful',
      eyes: 'Watching',
    },
  },
  comments: {
    show: 'Comments',
    hide: 'Hide comments',
    label: 'Your comment',
    empty: 'No comment yet.',
    submit: 'Comment',
    save: 'Save',
    saved: 'Comment updated.',
    edit: 'Edit my comment',
    delete: 'Delete my comment',
    deleted: 'Comment deleted.',
    edited: '(edited)',
  },
  follow: {
    follow: 'Follow',
    following: 'Following',
    followed: 'You follow “{{name}}”: its activity shows up in your feed.',
    unfollowed: 'You no longer follow “{{name}}”.',
    unavailable: 'Not available on this installation: the social features are turned off.',
    followers_one: '{{count}} follower',
    followers_other: '{{count}} followers',
  },
  errors: {
    forbidden: {
      follow: 'Your permissions do not allow following this resource.',
      react: 'Your permissions do not allow reacting to this activity.',
      comment: 'Your permissions do not allow commenting on this activity.',
      editComment: 'Only the author of a comment can edit it.',
      deleteComment: 'Only the author of a comment can delete it.',
    },
    notFound: {
      follow: 'This resource no longer exists, or you were not following it.',
      react: 'This activity no longer exists, or that reaction was not yours.',
      comment: 'This activity no longer exists.',
      editComment: 'This comment no longer exists.',
      deleteComment: 'This comment no longer exists.',
    },
    unavailable: 'Not available on this installation: the social features are turned off.',
    failed: 'That did not go through: {{detail}}',
  },
};

export const galleryEn: DeepStrings<typeof galleryFr> = {
  entry: 'Start from a template',
  title: 'Templates',
  subtitle:
    'Ready-to-fill pipelines shipped with graal: pick one, fill in its parameters, it opens in the editor.',
  searchPlaceholder: 'Search templates…',
  backToGraphs: 'Graphs',
  columns: {
    name: 'Template',
    description: 'What it does',
    latest: 'Latest version',
  },
  empty: {
    title: 'No template',
    description: 'This installation ships no low-code template.',
  },
  form: {
    graphName: 'Name of the new graph',
    graphNameHelp: 'Lowercase letters, digits and dashes. The template opens as a new, editable graph.',
    jsonPlaceholder: 'JSON value',
    errors: {
      array: 'A JSON array is expected, for instance [{"name": "date", "type": "str"}].',
      object: 'A JSON object is expected, for instance {"key": "value"}.',
    },
  },
  detail: {
    version: 'Version',
    parameters_one: '{{count}} parameter',
    parameters_other: '{{count}} parameters',
    formTitle: 'Fill in and open',
    submit: 'Open in the editor',
    footer: 'The rendered DAG opens in the canvas: nothing runs before “Create a job”.',
    graphDescription: 'Created from the template {{template}} {{version}}.',
    opened: 'Graph “{{name}}” created from the template.',
    importFailed: 'The rendered DAG cannot open in the editor (task {{task}}): {{reason}}',
  },
  importProblems: {
    notADag: 'the response is not a DAG.',
    unsupportedTask: 'task “{{detail}}” has no box in the editor.',
    badConnector: 'connector “{{detail}}” is not supported.',
    literalCredentials: 'key “{{detail}}” written in clear instead of a reference.',
    badParams: 'unreadable parameters ({{detail}}).',
    unknownDependency: 'unknown dependency “{{detail}}”.',
  },
  verify: {
    title: 'Check',
    action: 'Check',
    valid: 'The engine accepts this DAG',
    validHelp: 'Column schema of each task, and the frameworks able to run it.',
    frameworks: 'Compatible frameworks',
    noFramework: 'No framework can run this DAG.',
    schemas: 'Columns per task',
    unavailable: 'The low-code engine is not enabled on this installation.',
    forbidden:
      'Your permissions do not allow having a DAG checked by the engine (low-code execute permission).',
    refused: 'The engine refuses this DAG',
    refusedHelp: 'The detail below comes from the engine: it names the task or column at fault.',
  },
};
